import type { Tile } from '../schema/tiles.js';

/**
 * Result of a tile activation routed through the engine.
 *
 * Renderers surface failure reasons to the user (toast, badge, etc.) and the
 * engine logs them to the audit stream when `auditLog` is entitled. The
 * `notEntitled` reason is special: it indicates the runtime refused to act
 * because the license doesn't permit the feature (e.g., agentActions on
 * Core). This keeps the renderer dumb — it doesn't need to know tier rules.
 */
export type TileActivationResult =
  | { ok: true }
  | { ok: false; reason: 'notEntitled'; flag: string }
  | { ok: false; reason: 'unsupportedTileType'; type: string }
  | { ok: false; reason: 'agentError'; message: string };

/**
 * Snapshot of device + network state for the diagnostic / device-info UX.
 *
 * The shape is the union of "what the JS Agent API can supply on a real
 * device" and "what a WebView can derive on its own." Every field is
 * optional because each AgentHost implementation knows different things:
 *
 *   - Browser host (demo): UA-derived OS, screen, timezone, online flag,
 *     Network Information API estimates, storage usage. NO IMEI, SSID,
 *     carrier, etc. — those need MDM.
 *   - Production-on-device host: most fields populated from the SOTI JS
 *     Agent API.
 *   - OEM-extended hosts (Phase 4.5+): Zebra/Samsung/Honeywell add their
 *     own enriched values (e.g., real signalDbm from Zebra MX).
 *
 * The renderer's Device Info modal renders whatever is provided and
 * gracefully omits missing fields. Adding new optional fields here
 * doesn't break callers.
 */
export interface DeviceProperties {
  /** "phone" | "tablet" | "desktop" | "unknown". */
  formFactor?: 'phone' | 'tablet' | 'desktop' | 'unknown';
  /** Short OS name (e.g., "Android 14"). */
  os?: string;
  /** Short browser name + version when running inside a WebView. */
  browser?: string;
  /** BCP-47 locale, e.g., "en-US". */
  language?: string;
  /** IANA timezone, e.g., "America/Los_Angeles". */
  timezone?: string;
  /** Network reachability snapshot. */
  online?: boolean;

  /** CPU + memory. */
  hardwareConcurrency?: number;
  deviceMemoryGb?: number;
  screen?: { width: number; height: number; pixelRatio: number };

  /** Storage usage / quota in bytes. */
  storageUsageBytes?: number;
  storageQuotaBytes?: number;

  /** Network Information API style estimates. */
  connection?: {
    type?: string;
    effectiveType?: string;
    downlinkMbps?: number;
    rttMs?: number;
  };

  /** MDM-supplied identity — JS Agent API only. */
  deviceName?: string;
  /** OEM (e.g., "Zebra", "Samsung", "Honeywell"). MDM-supplied. */
  manufacturer?: string;
  /** Device model name. MDM-supplied. */
  model?: string;
  /** Hardware MAC address. MDM-supplied. */
  macAddress?: string;
  imei?: string;
  serialNumber?: string;

  /** Real radio info — JS Agent API only. */
  ssid?: string;
  signalDbm?: number;
  vpnConnected?: boolean;
  /** Cellular carrier name. MDM-supplied. */
  carrier?: string;
  /** Cellular roaming status. MDM-supplied. */
  roaming?: boolean;
  /** Whether the active network bills by usage. MDM-supplied. */
  metered?: boolean;
  /** Downstream bandwidth estimate, kbps. MDM-supplied. */
  downlinkKbps?: number;
  /** Upstream bandwidth estimate, kbps. MDM-supplied. */
  uplinkKbps?: number;

  /** Battery — MDM-supplied (modern browsers also have a Battery API
   *  but it's been removed from Chrome over fingerprinting concerns). */
  /** Charge level, 0–1. */
  batteryLevel?: number;
  batteryCharging?: boolean;
  /** Battery temperature in degrees Celsius. */
  batteryTemperatureC?: number;
}

/**
 * One installed application as reported by the agent's package inventory
 * (`mobicontrol.app.installedApps` / `getInstalledApp`). Only `packageName`
 * is guaranteed; the rest are best-effort and omitted when the agent
 * doesn't supply them.
 */
export interface InstalledApp {
  packageName: string;
  /** User-facing label, when the agent reports one. */
  name?: string;
  /** Human version string, e.g. "118.0.5993.48". */
  versionName?: string;
  /** Monotonic Android versionCode. */
  versionCode?: number;
  /** True for OS/system apps the user can't uninstall. */
  isSystem?: boolean;
}

/**
 * Filesystem + RAM snapshot from the agent's `storage` / `memory` views.
 * Spaces are in bytes. Every field is optional — a given device/agent
 * version may expose only a subset.
 */
export interface StorageInfo {
  internal?: {
    dataDirectory?: string;
    availableSpace?: number;
    totalSpace?: number;
  };
  external?: {
    rootDirectory?: string;
    dataDirectory?: string;
    documentsDirectory?: string;
    downloadsDirectory?: string;
    availableSpace?: number;
    totalSpace?: number;
  };
  /** RAM, via `mobicontrol.memory`. */
  memory?: {
    availableSpace?: number;
    totalSpace?: number;
  };
}

/** A directory listing entry from {@link AgentHost.listDir}. */
export interface FileEntry {
  path: string;
  isDirectory: boolean;
  /** Size in bytes (files only). */
  size?: number;
  /** Last-modified epoch millis, when reported. */
  lastModified?: number;
}

/**
 * The signed-in user on a shared device (`mobicontrol.sharedDevice`).
 * Feeds the deferred SSO status-bar display. All fields optional because
 * the agent may report a bare username string or a richer object.
 */
export interface DeviceUser {
  id?: string;
  name?: string;
  email?: string;
}

/** Severity for {@link AgentHost.log}; maps to `mobicontrol.log.*`. */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/** Options for {@link AgentHost.openUrl}. */
export interface OpenUrlOptions {
  /** Open in the device browser via intent rather than the in-launcher WebView. */
  external?: boolean;
  /** External-path tab behavior; 'new' (default) vs 'same' (reuse launcher's tab). */
  tab?: 'same' | 'new';
}

/**
 * The host abstraction over the SOTI JS Agent API. In production this is
 * implemented by a thin wrapper around `MDMSWebView` / `_MobiControlAgent`
 * (see GLOSSARY.md § "JS Agent API"). In Node tests we inject a fake.
 *
 * Methods are intentionally narrow — only what the launcher needs to drive
 * tile activations and lifecycle. Newer capabilities like
 * `getDeviceProperties` are optional: callers feature-detect rather than
 * forcing every host to stub them.
 */
export interface AgentHost {
  /** Launch a foreground app by package name. */
  launchApp(packageName: string): Promise<void>;

  /**
   * Open a URL. By default opens in the launcher's WebView; when
   * `options.external` is true, hosts that can launch the device's
   * default browser (e.g., AndroidIntentAgentHost) should do so.
   * Hosts that can't honor `external` treat it as a no-op and load
   * in-page — never refuse the call.
   *
   * `options.tab` only applies on the external path: 'new' opens a
   * fresh Chrome tab, 'same' reuses the launcher's existing tab.
   * Undefined behaves as 'new'.
   */
  openUrl(url: string, options?: OpenUrlOptions): Promise<void>;

  /** Reboot the device. The Agent typically requires admin privilege. */
  reboot(): Promise<void>;

  /** Clear app data for the given package; `undefined` = the launcher itself. */
  clearAppData(packageName: string | undefined): Promise<void>;

  /** Run a script identified by AssetRef (resolved by the Agent host). */
  runScript(scriptRef: string): Promise<void>;

  /** Trigger a sync with the MobiControl server. */
  syncNow(): Promise<void>;

  /**
   * Snapshot device + network properties for the diagnostic / device-info
   * UX. Optional: callers feature-detect with `host.getDeviceProperties`.
   */
  getDeviceProperties?(): Promise<DeviceProperties>;

  /**
   * Inventory the apps installed on the device. Powers app-tile pickers
   * and audit/compliance views. Only hosts backed by a real MDM agent
   * can supply this; browser/intent hosts omit it.
   */
  getInstalledApps?(): Promise<InstalledApp[]>;

  /** Internal/external storage + RAM usage from the agent. */
  getStorageInfo?(): Promise<StorageInfo>;

  /** Read a UTF-8 text file from the device filesystem via the agent. */
  readFile?(path: string): Promise<string>;

  /** Write a UTF-8 text file to the device filesystem via the agent. */
  writeFile?(path: string, contents: string): Promise<void>;

  /** List the entries of a directory on the device filesystem. */
  listDir?(path: string): Promise<FileEntry[]>;

  /**
   * Emit a log line to the agent, which surfaces it to the MobiControl
   * server. Best-effort and synchronous — never throws, so callers can
   * log freely without try/catch. No-ops when no agent is present.
   */
  log?(level: LogLevel, message: string): void;

  /** The currently signed-in shared-device user, or null if none. */
  getCurrentUser?(): Promise<DeviceUser | null>;

  /**
   * Read an Android system property (e.g. `ro.product.manufacturer`).
   * Underpins OEM detection without needing the OEM's own SDK. Resolves
   * undefined when the property is unset.
   */
  getSystemProperty?(name: string): Promise<string | undefined>;

  /**
   * Set the lock-task (kiosk) allowlist to exactly these packages — the app
   * tiles the current config can launch, so a pinned device stays pinned when
   * a tile opens another app. First-class because lock task is core to the
   * Android Enterprise / native-shell backbone. Optional: hosts without a
   * kiosk backend (browser, SOTI, un-enrolled) simply omit it, and the caller
   * feature-detects. Best-effort — a DPM hiccup must not fail boot.
   */
  setAllowedPackages?(packages: readonly string[]): Promise<void>;
}

/**
 * Test/dev AgentHost that records every call. Each method resolves
 * successfully unless `failNext` is set — useful for testing the engine's
 * error-surfacing behavior without simulating the full SOTI Agent surface.
 */
export class RecordingAgentHost implements AgentHost {
  readonly calls: { method: string; args: unknown[] }[] = [];
  failNext: { error: string } | null = null;

  private async route(method: string, args: unknown[]): Promise<void> {
    this.calls.push({ method, args });
    if (this.failNext) {
      const err = this.failNext.error;
      this.failNext = null;
      throw new Error(err);
    }
    return Promise.resolve();
  }

  launchApp(packageName: string): Promise<void> {
    return this.route('launchApp', [packageName]);
  }
  openUrl(url: string, options?: OpenUrlOptions): Promise<void> {
    return this.route('openUrl', options ? [url, options] : [url]);
  }
  reboot(): Promise<void> {
    return this.route('reboot', []);
  }
  clearAppData(packageName: string | undefined): Promise<void> {
    return this.route('clearAppData', [packageName]);
  }
  runScript(scriptRef: string): Promise<void> {
    return this.route('runScript', [scriptRef]);
  }
  syncNow(): Promise<void> {
    return this.route('syncNow', []);
  }
}

/** Helper: branch on tile type and call the matching AgentHost method. */
export async function dispatchToAgent(host: AgentHost, tile: Tile): Promise<void> {
  switch (tile.type) {
    case 'app':
      return host.launchApp(tile.packageName);
    case 'web':
      return host.openUrl(tile.url, {
        external: tile.openExternal,
        ...(tile.externalTab !== undefined ? { tab: tile.externalTab } : {}),
      });
    case 'spacer':
      throw new Error('cannot dispatch a spacer tile');
    case 'divider':
      throw new Error('cannot dispatch a divider tile');
    case 'folder':
      // Folders are navigational — the renderer opens the folder modal
      // and never routes a folder through here. If one slips through,
      // fail loudly rather than silently no-op.
      throw new Error('cannot dispatch a folder tile');
    case 'agentAction':
      switch (tile.action) {
        case 'reboot':
          return host.reboot();
        case 'clearAppData':
          return host.clearAppData(tile.targetPackage);
        case 'runScript':
          if (tile.scriptRef === undefined) {
            // The schema's runScript→scriptRef refine prevents this; if we
            // ever reach here, an internal contract is broken.
            throw new Error('runScript tile missing scriptRef');
          }
          return host.runScript(tile.scriptRef);
        case 'syncNow':
          return host.syncNow();
      }
  }
}
